import React from 'react';
import { resolveColores } from '../../constants/menuDigitalPlantillas';
import type {
  PublicMenu,
  PublicMenuCategory,
  PublicMenuProduct,
  PublicDailyMenu,
} from '../../types/menuDigital';

// Render de las tres plantillas del menú digital. Es la MISMA pieza que ve el
// comensal en /menu/{slug} y la que el dueño edita en Gestión del Menú: si
// fueran dos componentes, la "vista previa" derivaría de la página real y
// dejaría de servir para lo único que sirve (ver lo que ve el cliente).
//
// Sin AntD a propósito: los overrides globales móviles de App.css pegan sobre
// .ant-* con !important y desfigurarían la plantilla. Todo el estilo vive en
// styles/publicMenu.css y cuelga de 4 variables de color.
//
// El modo edición NO vive aquí: se inyecta con `renderProduct` /
// `renderCategoria`, que reciben el nodo ya armado y lo decoran. Así la
// plantilla se mantiene tonta y el editor no tiene que duplicar el maquetado.

export function formatPrecio(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? `$${rounded}` : `$${rounded.toFixed(2)}`;
}

export function formatModifier(value: number): string {
  const sign = value >= 0 ? '+' : '−';
  return `${sign}${formatPrecio(Math.abs(value))}`;
}

export interface MenuTemplateCategory<P extends PublicMenuProduct> extends Omit<PublicMenuCategory, 'products'> {
  products: P[];
}

/**
 * `available` queda fuera a propósito: es del contrato del endpoint público
 * (módulo apagado o tenant caído), no del render. Quien la recibe decide si
 * pinta la plantilla o la página de "no disponible"; la plantilla nunca la lee.
 */
export interface MenuTemplateData<P extends PublicMenuProduct>
  extends Omit<PublicMenu, 'categorias' | 'available'> {
  categorias: MenuTemplateCategory<P>[];
}

interface MenuTemplateProps<P extends PublicMenuProduct> {
  menu: MenuTemplateData<P>;
  soloHoy?: boolean;
  /** Decora el renglón de un producto (el editor mete aquí su checkbox y su asa de arrastre). */
  renderProduct?: (product: P, categoria: MenuTemplateCategory<P>, node: React.ReactNode) => React.ReactNode;
  /** Decora una sección de categoría completa. */
  renderCategoria?: (categoria: MenuTemplateCategory<P>, node: React.ReactNode) => React.ReactNode;
  /** El pie de "Menú digital por Massrestaurant" sobra dentro de la app. */
  showFooter?: boolean;
  /**
   * Llaves de React. El default es el nombre —único por categoría, que el
   * backend garantiza— pero el editor DEBE pasar los ids: dentro de una
   * superficie de arrastre, dos llaves iguales hacen que React reconcilie el
   * subárbol equivocado y la fila movida aparece con los datos de otra.
   */
  categoryKey?: (categoria: MenuTemplateCategory<P>) => string;
  productKey?: (product: P) => string;
}

/** Ancla legible y estable para el índice; el id no sirve como #hash bonito. */
function anchorId(nombre: string): string {
  return `sec-${nombre
    .normalize('NFD')
    // Escapes explícitos y no los caracteres combinantes literales: escritos
    // tal cual, cualquier paso que recodifique el archivo los rompe en silencio.
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')}`;
}

/**
 * Índice de secciones. Aparece a partir de 3 categorías: con menos, el menú
 * cabe en un vistazo y una barra de navegación solo estorbaría. En cartas
 * largas evita que el comensal tenga que raspar la pantalla hasta el postre.
 */
const IndiceSecciones: React.FC<{ nombres: string[] }> = ({ nombres }) => {
  if (nombres.length < 3) return null;
  return (
    <nav className="pm-indice" aria-label="Secciones del menú">
      {nombres.map((nombre) => (
        <a key={nombre} href={`#${anchorId(nombre)}`}>
          {nombre}
        </a>
      ))}
    </nav>
  );
};

const Modificadores: React.FC<{ product: PublicMenuProduct }> = ({ product }) => {
  if (!product.modificadores?.length) return null;
  return (
    <>
      {product.modificadores.map((grupo) => (
        <div key={grupo.name} className="pm-mods">
          <span className="pm-mods-grupo">{grupo.name}:</span>{' '}
          {grupo.options
            .map((o) => (o.priceModifier ? `${o.name} (${formatModifier(o.priceModifier)})` : o.name))
            .join(' · ')}
        </div>
      ))}
    </>
  );
};

function ProductoRenglon<P extends PublicMenuProduct>({ producto }: { producto: P }) {
  return (
    <>
      <div className="pm-fila">
        <span className="pm-nombre">{producto.name}</span>
        <span className="pm-puntos" />
        <span className="pm-precio">{formatPrecio(producto.price)}</span>
      </div>
      {producto.description && <div className="pm-desc">{producto.description}</div>}
      <Modificadores product={producto} />
    </>
  );
}

const DailyMenuBlock: React.FC<{ menu: PublicDailyMenu }> = ({ menu }) => (
  <div className="pm-deldia">
    <span className="pm-deldia-hoy">Hoy</span>
    <span className="pm-deldia-titulo">
      {menu.name} · {formatPrecio(menu.price)}
    </span>
    {menu.description && <p className="pm-deldia-linea">{menu.description}</p>}
    {menu.courses.map((course) => (
      <p key={course.name} className="pm-deldia-linea">
        {course.isSelectable && course.options.length > 0
          ? `${course.name}: ${course.options.map((o) => o.name).join(' / ')}`
          : `${course.name}${course.fixedDescription ? `: ${course.fixedDescription}` : ''}`}
      </p>
    ))}
  </div>
);

const HoyContent: React.FC<{ menu: MenuTemplateData<PublicMenuProduct> }> = ({ menu }) => (
  <>
    {menu.fechaLabel && <p className="pm-fecha">{menu.fechaLabel}</p>}
    {menu.menusDelDia.length === 0 && (
      <p className="pm-catdesc" style={{ marginTop: 24 }}>
        Hoy no hay menú del día publicado. Pregunta en el mostrador.
      </p>
    )}
    {menu.menusDelDia.map((dailyMenu) => (
      <article key={dailyMenu.name}>
        <p className="pm-platon">
          {dailyMenu.name} · {formatPrecio(dailyMenu.price)}
        </p>
        {dailyMenu.description && <p className="pm-incluye">{dailyMenu.description}</p>}
        {dailyMenu.courses.map((course) => (
          <section key={course.name}>
            <hr className="pm-raya" />
            <h2 className="pm-cat">{course.name}</h2>
            {course.isSelectable ? (
              course.options.map((option) => (
                <div key={option.name} className="pm-item">
                  <div className="pm-fila">
                    <span className="pm-nombre">{option.name}</span>
                    {option.priceModifier !== 0 && (
                      <span className="pm-precio">{formatModifier(option.priceModifier)}</span>
                    )}
                  </div>
                  {option.description && <div className="pm-desc">{option.description}</div>}
                </div>
              ))
            ) : (
              course.fixedDescription && <p className="pm-desc">{course.fixedDescription}</p>
            )}
          </section>
        ))}
      </article>
    ))}
  </>
);

export function MenuTemplate<P extends PublicMenuProduct>({
  menu,
  soloHoy = false,
  renderProduct,
  renderCategoria,
  showFooter = true,
  categoryKey = (c) => c.name,
  productKey = (p) => p.name,
}: MenuTemplateProps<P>) {
  const colores = resolveColores(menu.plantilla, menu.colores);
  const vars = {
    '--c1': colores[0],
    '--c2': colores[1],
    '--c3': colores[2],
    '--c4': colores[3],
  } as React.CSSProperties;

  const nombrePartido = menu.plantilla === 'moderna' ? menu.restaurantName.split(' ') : [];
  const mitad = Math.ceil(nombrePartido.length / 2);

  const logo = menu.logoBase64 && menu.logoMimeType && (
    <img className="pm-logo" src={`data:${menu.logoMimeType};base64,${menu.logoBase64}`} alt="" />
  );

  const categoriaBlock = (categoria: MenuTemplateCategory<P>) => {
    const catKey = categoryKey(categoria);
    const node = (
      <section key={catKey} id={anchorId(categoria.name)}>
        <h2 className="pm-cat">{categoria.name}</h2>
        {categoria.description && <p className="pm-catdesc">{categoria.description}</p>}
        {categoria.products.map((producto) => {
          const prodKey = productKey(producto);
          const renglon = (
            <div key={prodKey} className="pm-item">
              <ProductoRenglon producto={producto} />
            </div>
          );
          return renderProduct ? (
            <React.Fragment key={prodKey}>
              {renderProduct(producto, categoria, renglon)}
            </React.Fragment>
          ) : (
            renglon
          );
        })}
      </section>
    );
    return renderCategoria ? (
      <React.Fragment key={catKey}>{renderCategoria(categoria, node)}</React.Fragment>
    ) : (
      node
    );
  };

  const pie = (menu.address || menu.phone) && (
    <p className="pm-pie">
      {[menu.address, menu.phone].filter(Boolean).join(' · ')}
      {menu.plantilla === 'carta' ? <br /> : ' · '}
      Precios en pesos, IVA incluido
    </p>
  );

  // El índice solo tiene sentido en la carta completa: la vista del día ya es
  // corta por definición.
  const indice = soloHoy ? null : <IndiceSecciones nombres={menu.categorias.map((c) => c.name)} />;

  const cuerpo = soloHoy ? (
    <HoyContent menu={menu} />
  ) : menu.plantilla === 'carta' ? (
    <>
      {indice}
      {menu.categorias.map(categoriaBlock)}
      {menu.menusDelDia.map((dailyMenu) => (
        <div key={dailyMenu.name} className="pm-deldia">
          <span className="pm-deldia-titulo">Menú del día · {formatPrecio(dailyMenu.price)}</span>
          <p className="pm-deldia-linea">
            {dailyMenu.description ??
              dailyMenu.courses
                .map((c) =>
                  c.isSelectable && c.options.length > 0
                    ? c.options.map((o) => o.name).join(' / ')
                    : c.fixedDescription ?? c.name
                )
                .join(' · ')}
          </p>
        </div>
      ))}
    </>
  ) : (
    <>
      {menu.menusDelDia.map((dailyMenu) => (
        <DailyMenuBlock key={dailyMenu.name} menu={dailyMenu} />
      ))}
      {indice}
      {menu.categorias.map(categoriaBlock)}
    </>
  );

  return (
    <div className={`pm-root pm-${menu.plantilla}`} style={vars}>
      <div className="pm-sheet">
        {menu.plantilla === 'moderna' ? (
          <header className="pm-portada">
            {logo}
            <h1 className="pm-resto">
              {nombrePartido.slice(0, mitad).join(' ')}
              {nombrePartido.length > mitad && <em>{nombrePartido.slice(mitad).join(' ')}</em>}
            </h1>
            {menu.lema && <p className="pm-lema">{menu.lema}</p>}
          </header>
        ) : (
          <header>
            {logo}
            <h1 className="pm-resto">{menu.restaurantName}</h1>
            {menu.lema && <p className="pm-lema">{menu.lema}</p>}
          </header>
        )}

        {menu.plantilla === 'carta' ? (
          <div className="pm-marco" style={{ marginTop: 20 }}>
            {cuerpo}
            {pie}
          </div>
        ) : (
          <>
            {cuerpo}
            {pie}
          </>
        )}

        {showFooter && (
          <footer className="pm-footer">
            <a href="https://www.massresto.com" target="_blank" rel="noreferrer">
              Menú digital por Massrestaurant
            </a>
          </footer>
        )}
      </div>

    </div>
  );
}

export default MenuTemplate;
